import { useMemo, useState } from 'react'
import clsx from 'clsx'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useDrillDown } from '@/components/dashboards/useDrillDown'
import { PanelSkeleton } from '@/components/dashboards/Panel'
import { ANALYTICS_KEY } from '@/components/analytics/explorer/analyticsQueries'
import {
  OUTCOME_COLOR,
  compactCurrency,
  dashboardAnalyticsService,
  drillForIds,
  type DealFact,
} from '@/services/dashboardAnalytics'

function shortDate(value: string | null | undefined): string {
  if (!value) return '--'
  const [y, m, d] = value.slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return '--'
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
}

function pct(value: number | null | undefined): string {
  return value == null ? '--' : `${value.toFixed(1)}%`
}

interface StageGroup {
  key: string
  label: string
  rank: number
  deals: DealFact[]
  volume: number | null
}

/** Pure: deals grouped by stage in pipeline order — Triage first, the way an
 *  originator reads their own book (T-63) — dead last. Tested. */
export function groupByStage(deals: readonly DealFact[]): StageGroup[] {
  const groups = new Map<string, StageGroup>()
  for (const deal of deals) {
    const dead = deal.outcome === 'dead'
    const key = dead ? 'Dead' : deal.stage ?? 'Stage not recorded'
    const group =
      groups.get(key) ??
      {
        key,
        label: dead ? 'Dead' : deal.stageLabel ?? deal.stage ?? 'Stage not recorded',
        rank: dead ? 99 : deal.stageRank ?? 98,
        deals: [],
        volume: null,
      }
    group.deals.push(deal)
    const amount = deal.commitmentAmount ?? deal.requestedAmount
    if (amount != null) group.volume = (group.volume ?? 0) + amount
    groups.set(key, group)
  }
  for (const group of groups.values()) {
    group.deals.sort((a, b) => (b.daysInStatus ?? -1) - (a.daysInStatus ?? -1) || (a.name ?? '').localeCompare(b.name ?? ''))
  }
  return [...groups.values()].sort((a, b) => a.rank - b.rank)
}

function DealRows({ deals, title }: { deals: readonly DealFact[]; title: string }) {
  const { open } = useDrillDown()
  const groups = useMemo(() => groupByStage(deals), [deals])
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  if (deals.length === 0) {
    return <p className="px-3 py-4 text-[11px] text-stone-500">No deal.</p>
  }
  return (
    <table className="dense-table">
      <thead className="sticky top-0 z-[1]">
        <tr>
          <th className="!px-2">Deal</th>
          <th className="!px-2 text-right" title="Whole 24-hour days in the current status">In status</th>
          <th className="!px-2 text-right">Commitment</th>
          <th className="hidden !px-2 text-right lg:table-cell">LTV/LTC</th>
          <th className="hidden !px-2 text-right xl:table-cell">Coupon</th>
          <th className="hidden !px-2 lg:table-cell">Brokerage</th>
          <th className="hidden !px-2 min-[1900px]:table-cell">Sponsor</th>
          <th className="!px-2 text-right">Received</th>
          <th className="hidden !px-2 text-right min-[1700px]:table-cell">Funded</th>
        </tr>
      </thead>
      {groups.map((group) => {
        const isCollapsed = collapsed.has(group.key)
        return (
          <tbody key={group.key}>
            <tr className="bg-[#0d0c0a]">
              <td colSpan={9} className="!px-2 !py-1">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setCollapsed((current) => {
                        const next = new Set(current)
                        if (next.has(group.key)) next.delete(group.key)
                        else next.add(group.key)
                        return next
                      })
                    }
                    aria-expanded={!isCollapsed}
                    className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.16em] text-amber-200/80 hover:text-amber-100"
                  >
                    {isCollapsed ? <ChevronRight size={10} /> : <ChevronDown size={10} />}
                    {group.label}
                  </button>
                  <button
                    type="button"
                    onClick={() => open(drillForIds(group.deals.map((deal) => deal.id), `${title} — ${group.label}`))}
                    className="font-mono text-[10px] tabular-nums text-stone-300 underline decoration-dotted decoration-amber-400/25 underline-offset-[3px] hover:text-amber-200"
                  >
                    {group.deals.length} deal{group.deals.length === 1 ? '' : 's'}
                  </button>
                  <span className="font-mono text-[10px] tabular-nums text-stone-500">{compactCurrency(group.volume)}</span>
                </div>
              </td>
            </tr>
            {!isCollapsed &&
              group.deals.map((deal) => (
                <tr key={deal.id} className="cursor-pointer" onClick={() => open(drillForIds([deal.id], deal.name ?? deal.id))}>
                  <td className="max-w-[220px] !px-2">
                    <span className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ background: OUTCOME_COLOR[deal.outcome] }} aria-hidden="true" />
                      <span className="truncate text-[11px] text-stone-100" title={deal.name ?? deal.id}>
                        {deal.name ?? deal.id}
                      </span>
                    </span>
                    <span className="block truncate pl-3 text-[9px] text-stone-600">
                      {[deal.propertyType, [deal.city, deal.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ') || deal.id}
                    </span>
                  </td>
                  <td className={clsx('!px-2 text-right font-mono text-[10.5px] tabular-nums', (deal.daysInStatus ?? 0) >= 14 ? 'text-amber-300' : 'text-stone-300')}>
                    {deal.daysInStatus == null ? '--' : `${deal.daysInStatus}d`}
                  </td>
                  <td className="!px-2 text-right font-mono text-[10.5px] tabular-nums text-stone-200">
                    {compactCurrency(deal.commitmentAmount ?? deal.requestedAmount)}
                  </td>
                  <td className="hidden !px-2 text-right font-mono text-[10.5px] tabular-nums text-stone-400 lg:table-cell">
                    {pct(deal.ltvLtc)}
                    {deal.ltvLtcBasis && deal.ltvLtc != null && (
                      <span className="ml-0.5 text-[8px] uppercase text-stone-600">{deal.ltvLtcBasis === 'ltvLtc' ? 'ltv/ltc' : deal.ltvLtcBasis}</span>
                    )}
                  </td>
                  <td className="hidden max-w-[110px] truncate !px-2 text-right font-mono text-[10px] text-stone-400 xl:table-cell" title={deal.couponText ?? undefined}>
                    {deal.couponText ?? pct(deal.couponPct)}
                  </td>
                  <td className="hidden max-w-[140px] truncate !px-2 text-[10.5px] text-stone-400 lg:table-cell" title={deal.brokerage ?? undefined}>
                    {deal.brokerage ?? '--'}
                  </td>
                  <td className="hidden max-w-[140px] truncate !px-2 text-[10.5px] text-stone-400 min-[1900px]:table-cell" title={deal.sponsor ?? undefined}>
                    {deal.sponsor ?? '--'}
                  </td>
                  <td className="!px-2 text-right font-mono text-[10px] tabular-nums text-stone-400">{shortDate(deal.receivedDate)}</td>
                  <td className="hidden !px-2 text-right font-mono text-[10px] tabular-nums text-stone-400 min-[1700px]:table-cell">{shortDate(deal.fundedDate)}</td>
                </tr>
              ))}
          </tbody>
        )
      })}
    </table>
  )
}

interface PersonDealsProps {
  name: string
  /** Deals the person holds the role on (the profile's `deals`). */
  holds: readonly DealFact[]
  holdsLabel: string
  /** Deals they support without being Responsible Party (T-55); ids only. */
  supportingIds?: readonly string[]
  supportingBasis?: string
}

/**
 * "My deals" (T-55): the deals the person is responsible for and the deals
 * they support, each split by stage, Triage first.
 */
export default function PersonDeals({ name, holds, holdsLabel, supportingIds = [], supportingBasis }: PersonDealsProps) {
  const [tab, setTab] = useState<'holds' | 'supporting'>('holds')
  const supporting = useQuery({
    queryKey: [...ANALYTICS_KEY, 'dealsById', [...supportingIds].sort().join(',')],
    queryFn: ({ signal }) => dashboardAnalyticsService.dealsById([...supportingIds], signal),
    enabled: tab === 'supporting' && supportingIds.length > 0,
    staleTime: 30_000,
  })

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-shrink-0 items-center gap-1 border-b border-amber-400/[0.06] px-2 py-1.5" role="tablist">
        {(
          [
            ['holds', `${holdsLabel} · ${holds.length}`],
            ['supporting', `Supporting · ${supportingIds.length}`],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={clsx(
              'rounded-full border px-2 py-0.5 text-[10px] transition-colors',
              tab === key ? 'border-amber-400/35 bg-amber-400/[0.08] text-amber-200' : 'border-amber-400/10 text-stone-500 hover:text-stone-300',
            )}
          >
            {label}
          </button>
        ))}
        {tab === 'supporting' && supportingBasis && (
          <span className="ml-2 text-[9.5px] leading-snug text-stone-500">{supportingBasis}</span>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-auto scrollbar-thin">
        {tab === 'holds' ? (
          <DealRows deals={holds} title={`${name} — ${holdsLabel.toLowerCase()}`} />
        ) : supportingIds.length === 0 ? (
          <p className="px-3 py-4 text-[11px] text-stone-500">
            No deal names {name} as Underwriter, Secondary Underwriter, Analyst or Originator with someone else as
            Responsible Party. The deal book import carries no secondary underwriter, analyst or originator, so support
            work is not on record yet.
          </p>
        ) : supporting.isLoading ? (
          <div className="p-3">
            <PanelSkeleton rows={6} />
          </div>
        ) : (
          <DealRows deals={supporting.data?.deals ?? []} title={`${name} — supporting`} />
        )}
      </div>
    </div>
  )
}
